import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
// Anciennes feuilles gardées : sans découpage du code, elles s'appliquent à toute l'application (classes génériques
// .btn, .card, .empty-state…) ; leur nettoyage est un chantier à part. La page refaite utilise annonces-c.css.
import '@/styles/annonces.css';
import '@/styles/annonces-overrides.css';
import '@/styles/modern-cards.css';
import '@/styles/skeletons.css';
import '@/styles/annonces-c.css';
import ModernAnnonceCard from '@/components/ModernAnnonceCard';
import PublishButton from '@/components/PublishButton';
import { ModernCardSkeleton } from '@/components/Skeletons';
import { ccySym, TRANSPORT_LABELS } from '@/utils/listingParsers';
import { SUPPORTED_CURRENCIES } from '@/utils/currencyUtils';
import { tripPriceDisplay, ZERO_DEC_CCY, STATIC_RATES_EUR } from '@/utils/tripPriceUtils';
import CurrencyService from '@/services/CurrencyService';
import { cityFlag as flag } from '@/utils/cityFlag';
import { isEntreprise, isEntreprisePending } from '@/utils/entreprise';
import { compareAnnonces, defaultFilterType, isProTrip, matchesFilters, SORT_LABELS } from '@/utils/annoncesFilters';
import { crossRoleLink, publishTarget, roleRequestPath, ROLE_LABELS } from '@/utils/roles';
import { BACKEND_URL } from '@/config';
import { ArrowDownUp, Check, CloudOff, LoaderCircle, Search, SearchX, SlidersHorizontal, X } from 'lucide-react';

export { compareAnnonces, isProTrip };

const API = `${BACKEND_URL}/api`;
// Colis et trajets : au-delà, la page affiche l'échec et « Réessayer » au lieu d'attendre indéfiniment (T34)
const LOAD_TIMEOUT_MS = 15000;
const PAGE_SIZE = 15;
const CITIES = ['Dakar', 'Abidjan', 'Lagos', 'Bamako', 'Conakry', 'Douala', 'Casablanca', 'Accra'];
const MODES = ['avion', 'voiture', 'bateau', 'bus', 'train'];
const sansEmoji = (label) => String(label || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();

const initials = (f, l) => `${(f || '?')[0]}${(l || '?')[0]}`.toUpperCase();

function daysFromNow(dateIso) {
  if (!dateIso) return '·';
  const diff = Math.ceil((new Date(dateIso) - Date.now()) / 86400000);
  if (diff < 0) return `J${diff}`;
  if (diff === 0) return 'Auj.';
  return `J-${diff}`;
}
function makeRef(id, type) {
  if (!id) return '·';
  return `${type === 'trajet' ? 'C2S-TRJ' : 'C2S-COL'}-${String(id).slice(-4).toUpperCase()}`;
}
function normalize(item, type) {
  const user = item.author || item.owner || {};
  const score = user.avg_rating ? user.avg_rating.toFixed(1).replace('.', ',') : null;
  return {
    raw: item, id: item.id, type,
    from: item.departure_city, to: item.arrival_city,
    days: daysFromNow(item.departure_date),
    daysNum: !item.departure_date ? null : Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000),
    dateLimit: new Date(item.departure_date).toLocaleDateString('fr-FR'),
    price: type === 'trajet' ? tripPriceDisplay(item).amount : item.price_offered,
    unit: type === 'trajet' ? tripPriceDisplay(item).unit : ccySym(item.primary_currency || 'EUR'),
    weight: type === 'trajet' ? item.available_weight_kg : item.weight_kg,
    hot: (() => { const d = Math.ceil((new Date(item.departure_date) - Date.now()) / 86400000); return d > 0 && d <= 5; })(),
    ref: makeRef(item.id, type),
    user: {
      id: item.user_id,
      name: `${user.first_name || 'Utilisateur'} ${(user.last_name_initial || user.last_name || '').slice(0, 1).toUpperCase()}.`.trim(),
      ini: initials(user.first_name, user.last_name_initial || user.last_name),
      score: score || 'Nouveau', trips: user.rating_count || 0,
      verified: user.is_verified || false, pro: isEntreprise(user), proPending: isEntreprisePending(user),
      subscribed: !!user.pro_subscribed,
    },
  };
}

/** Ce que cherche chaque onglet : libellés de la phrase des résultats, du poids et de l'encart du bas (bidirectionnel). */
const TABS = {
  trajet: { tab: 'Transporteurs', noun: 'transporteurs', weight: 'Mon colis pèse' },
  colis: { tab: 'Colis', noun: 'colis', weight: 'Ma place libre' },
  all: { tab: 'Tous', noun: 'annonces', weight: 'Poids' },
};

/* ════════════════════════════════════════════════════
   PAGE PRINCIPALE — direction C « Lien » (docs/design/ecarts-page-annonces.md, 2026-10-29)
════════════════════════════════════════════════════ */
export default function AnnoncesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState({ active_total: 0, members: 0, delivered: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem('c2s_favs') || '[]'); } catch { return []; } });
  const [favNotif, setFavNotif] = useState(null);
  const [propCounts, setPropCounts] = useState({ shipments: {}, trips: {} });
  const [sPage, setSPage] = useState(1);
  const [tPage, setTPage] = useState(1);
  const [sHasMore, setSHasMore] = useState(false);
  const [tHasMore, setTHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterType, setFilterTypeState] = useState(() => defaultFilterType(user));
  const typeChosen = useRef(false);
  const setFilterType = (t) => { typeChosen.current = true; setFilterTypeState(t); };
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fDate, setFDate] = useState('');
  const [fWeight, setFWeight] = useState('');
  const [maxPrice, setMaxPrice] = useState(15000);
  const [fVerif, setFVerif] = useState(false);
  const [fPro, setFPro] = useState(false);
  const [fSub, setFSub] = useState(false);
  const [fModes, setFModes] = useState([]);
  const [filterCurrencies, setFilterCurrencies] = useState([]); // [] = pas de filtre devise
  const [filterRates, setFilterRates] = useState(STATIC_RATES_EUR);
  // Devise principale pour le curseur (1re sélectionnée, ou EUR par défaut)
  const filterCurrency = filterCurrencies[0] || 'EUR';
  const [sortBy, setSortBy] = useState('recommended');
  const [filterOpen, setFilterOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Onglet par défaut selon le rôle, une fois le compte connu — tant que le visiteur n'a rien choisi
  useEffect(() => {
    if (!typeChosen.current) setFilterTypeState(defaultFilterType(user));
  }, [user]);

  // Restaure les filtres actifs quand on revient depuis la page d'une annonce
  useEffect(() => {
    const f = location.state?.restoreFilters;
    if (!f) return;
    setFilterType(f.filterType ?? 'all');
    setFFrom(f.fFrom ?? '');
    setFTo(f.fTo ?? '');
    setFDate(f.fDate ?? '');
    setFWeight(f.fWeight ?? '');
    setFilterCurrencies(f.filterCurrencies ?? []);
    setMaxPrice(f.maxPrice ?? 15000);
    setFVerif(f.fVerif ?? false);
    setFPro(f.fPro ?? false);
    setFSub(f.fSub ?? false);
    setFModes(f.fModes ?? []);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Taux du jour pour le curseur de prix (secours statique déjà en place via STATIC_RATES_EUR)
  useEffect(() => {
    CurrencyService.getRates()
      .then(data => { if (data?.rates) setFilterRates(r => ({ ...r, ...data.rates })); })
      .catch(() => {});
  }, []);

  // Bornes du curseur dans la devise choisie (de l'équivalent de 1 € à 15 000 €)
  const { filterRate, sliderMin, sliderMax } = useMemo(() => {
    const rate = filterRates[filterCurrency] ?? 1;
    const zd = ZERO_DEC_CCY.has(filterCurrency);
    return {
      filterRate: rate,
      sliderMin: zd ? Math.ceil(rate) : Math.round(rate * 100) / 100,
      sliderMax: zd ? Math.floor(rate * 15000) : Math.round(rate * 15000 * 100) / 100,
    };
  }, [filterCurrency, filterRates]);

  // Quand la devise principale change, le curseur revient au maximum
  useEffect(() => {
    setMaxPrice(sliderMax);
  }, [filterCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  // « Recommandés » : l'API renvoie les trajets PRO en tête ; les tris explicites repartent de l'ordre de départ
  const featured = sortBy === 'recommended';
  const tripsUrl = useCallback(
    (page) => `${API}/public/trips?limit=${PAGE_SIZE}&page=${page}&featured=${featured}`,
    [featured],
  );

  // Chiffres du haut de page : chargés à part, ils ne bloquent jamais la liste (T34) ; en cas d'échec, valeurs par défaut
  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/public/stats`).then((r) => { if (!cancel) setStats(r.data); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/public/shipments?limit=${PAGE_SIZE}&page=1`, { timeout: LOAD_TIMEOUT_MS }),
          axios.get(tripsUrl(1), { timeout: LOAD_TIMEOUT_MS }),
        ]);
        if (cancel) return;
        const sList = s.data.data.map(x => normalize(x, 'colis'));
        const tList = t.data.data.map(x => normalize(x, 'trajet'));
        setShipments(sList); setTrips(tList);
        setSHasMore(!!s.data?.meta?.has_more); setTHasMore(!!t.data?.meta?.has_more);
        setSPage(1); setTPage(1);
        try {
          const sIds = sList.map(x => x.id).join(',');
          const tIds = tList.map(x => x.id).join(',');
          const c = await axios.get(`${API}/propositions/counts?shipment_ids=${sIds}&trip_ids=${tIds}`);
          if (!cancel) setPropCounts(c.data);
        } catch { /* compteurs facultatifs */ }
      } catch {
        if (!cancel) setLoadError(true);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bascule « Recommandés » ↔ tri explicite : recharge les trajets depuis la page 1 dans le bon ordre
  const featuredLoaded = useRef(featured);
  useEffect(() => {
    if (featuredLoaded.current === featured) return undefined;
    featuredLoaded.current = featured;
    let cancel = false;
    axios.get(tripsUrl(1)).then(async (t) => {
      if (cancel) return;
      const tList = (t.data?.data || []).map(x => normalize(x, 'trajet'));
      setTrips(tList); setTPage(1); setTHasMore(!!t.data?.meta?.has_more);
      try {
        const c = await axios.get(`${API}/propositions/counts?shipment_ids=&trip_ids=${tList.map(x => x.id).join(',')}`);
        if (!cancel) setPropCounts(prev => ({ ...prev, trips: { ...prev.trips, ...(c.data.trips || {}) } }));
      } catch { /* compteurs facultatifs */ }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [featured, tripsUrl]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const fetches = [];
      const nextS = sPage + 1, nextT = tPage + 1;
      if ((filterType === 'all' || filterType === 'colis') && sHasMore)
        fetches.push(axios.get(`${API}/public/shipments?limit=${PAGE_SIZE}&page=${nextS}`).then(r => ({ kind: 'colis', r, page: nextS })));
      if ((filterType === 'all' || filterType === 'trajet') && tHasMore)
        fetches.push(axios.get(tripsUrl(nextT)).then(r => ({ kind: 'trajet', r, page: nextT })));
      const results = await Promise.all(fetches);
      const newSIds = [], newTIds = [];
      for (const { kind, r, page } of results) {
        const items = (r.data?.data || []).map(x => normalize(x, kind));
        if (kind === 'colis') { setShipments(prev => [...prev, ...items]); setSPage(page); setSHasMore(!!r.data?.meta?.has_more); newSIds.push(...items.map(i => i.id)); }
        else { setTrips(prev => [...prev, ...items]); setTPage(page); setTHasMore(!!r.data?.meta?.has_more); newTIds.push(...items.map(i => i.id)); }
      }
      if (newSIds.length || newTIds.length) {
        try {
          const c = await axios.get(`${API}/propositions/counts?shipment_ids=${newSIds.join(',')}&trip_ids=${newTIds.join(',')}`);
          setPropCounts(prev => ({ shipments: { ...prev.shipments, ...(c.data.shipments || {}) }, trips: { ...prev.trips, ...(c.data.trips || {}) } }));
        } catch { /* compteurs facultatifs */ }
      }
    } finally { setLoadingMore(false); }
  }, [sPage, tPage, sHasMore, tHasMore, filterType, tripsUrl]);

  useEffect(() => {
    if (loading) return;
    const qv = new URLSearchParams(location.search).get('qv');
    if (!qv) return;
    const [qvId, qvType] = qv.split(':');
    if (qvId && qvType) {
      window.history.replaceState({}, '', location.pathname);
      navigate('/annonces/' + qvType + '/' + qvId, { replace: true });
    }
  }, [loading, location, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = () => {
    setFFrom(''); setFTo(''); setFDate(''); setFWeight('');
    setFilterCurrencies([]); setMaxPrice(15000);
    setFVerif(false); setFPro(false); setFSub(false); setFModes([]);
  };

  useEffect(() => {
    const type = new URLSearchParams(location.search).get('type');
    if (type === 'colis' || type === 'trajet') {
      setFilterType(type);
      resetFilters();
      window.history.replaceState({}, '', location.pathname);
      document.getElementById('cards-section')?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const toggleFav = useCallback((id, item) => {
    // Rôle requis : un transporteur garde des colis, un expéditeur des trajets
    if (item && user) {
      if (item.type === 'colis' && !user.roles?.includes('ROLE_CARRIER')) {
        setFavNotif(n => { if (n?.timer) clearTimeout(n.timer); return { kind: 'refused', neededRole: 'ROLE_CARRIER', item }; });
        return;
      }
      if (item.type === 'trajet' && !user.roles?.includes('ROLE_SENDER')) {
        setFavNotif(n => { if (n?.timer) clearTimeout(n.timer); return { kind: 'refused', neededRole: 'ROLE_SENDER', item }; });
        return;
      }
    }
    setFavs(prev => {
      const isAdding = !prev.includes(id);
      const next = isAdding ? [...prev, id] : prev.filter(x => x !== id);
      localStorage.setItem('c2s_favs', JSON.stringify(next));
      if (isAdding && item) {
        setFavNotif(n => {
          if (n?.timer) clearTimeout(n.timer);
          const timer = setTimeout(() => setFavNotif(null), 5000);
          return { kind: 'added', item, timer };
        });
      } else {
        setFavNotif(n => { if (n?.timer) clearTimeout(n.timer); return null; });
      }
      return next;
    });
  }, [user]);

  const isPriceActive = maxPrice < sliderMax;
  const combined = useMemo(() => {
    const f = {
      from: fFrom, to: fTo, beforeDate: fDate, weightKg: fWeight, currencies: filterCurrencies,
      maxPriceEur: isPriceActive ? maxPrice / filterRate : null, rates: filterRates,
      verified: fVerif, entreprise: fPro, subscribed: fSub, modes: fModes,
    };
    const keep = (list) => list.filter(a => matchesFilters(a, f)).sort((a, b) => compareAnnonces(sortBy, a, b));
    if (filterType === 'colis') return keep(shipments);
    if (filterType === 'trajet') return keep(trips);
    // Tous : colis et trajets mêlés, à proportion de leur nombre
    const c = keep(shipments), t = keep(trips);
    const out = [];
    const ratio = Math.max(1, Math.round(c.length / Math.max(t.length, 1)));
    let ci = 0, ti = 0;
    while (ci < c.length || ti < t.length) { for (let k = 0; k < ratio && ci < c.length; k++) out.push(c[ci++]); if (ti < t.length) out.push(t[ti++]); }
    return out;
  }, [shipments, trips, filterType, fFrom, fTo, fDate, fWeight, maxPrice, isPriceActive, fVerif, fPro, fSub, fModes, sortBy, filterRate, filterRates, filterCurrencies]);

  const tab = TABS[filterType] || TABS.all;
  const activeChips = useMemo(() => {
    const chips = [];
    if (fFrom) chips.push({ key: 'from', label: `Départ : ${fFrom}`, clear: () => setFFrom('') });
    if (fTo) chips.push({ key: 'to', label: `Arrivée : ${fTo}`, clear: () => setFTo('') });
    if (fDate) chips.push({ key: 'date', label: `Avant le ${fDate.split('-').reverse().join('/')}`, clear: () => setFDate('') });
    if (Number(fWeight) > 0) chips.push({ key: 'weight', label: `${tab.weight} : ${Number(fWeight).toLocaleString('fr-FR')} kg`, clear: () => setFWeight('') });
    if (filterCurrencies.length > 0) chips.push({ key: 'currencies', label: filterCurrencies.join(' + '), clear: () => setFilterCurrencies([]) });
    if (isPriceActive) chips.push({ key: 'price', label: `Max ${maxPrice.toLocaleString('fr-FR')} ${ccySym(filterCurrency)}`, clear: () => setMaxPrice(sliderMax) });
    if (fVerif) chips.push({ key: 'verif', label: 'Vérifié', clear: () => setFVerif(false) });
    if (fPro) chips.push({ key: 'pro', label: 'Entreprise', clear: () => setFPro(false) });
    if (fSub) chips.push({ key: 'sub', label: 'Abonné PRO', clear: () => setFSub(false) });
    fModes.forEach((m) => chips.push({ key: `mode-${m}`, label: sansEmoji(TRANSPORT_LABELS[m]), clear: () => setFModes(prev => prev.filter(x => x !== m)) }));
    return chips;
  }, [fFrom, fTo, fDate, fWeight, tab.weight, maxPrice, isPriceActive, fVerif, fPro, fSub, fModes, filterCurrency, filterCurrencies, sliderMax]);

  const scrollToResults = () => document.getElementById('cards-section')?.scrollIntoView({ behavior: 'smooth' });
  const quickSearch = (city) => { setFTo(city); scrollToResults(); };
  // Où mènent les liens de publication : inscription, formulaire ou demande du rôle manquant (utils/roles.js)
  const crossLink = crossRoleLink(user);
  const toggleMode = (m) => setFModes(prev => (prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]));
  const fromFilters = { filterType, fFrom, fTo, fDate, fWeight, filterCurrencies, maxPrice, fVerif, fPro, fSub, fModes };

  return (
    <div className="annonces-page an-c" data-testid="annonces-page">
      <header className="an-hdr">
        <nav className="an-nav" aria-label="Navigation principale">
          <Link className="an-brand" to="/">colis<span className="an-brand-2">2</span>ship</Link>
          <div className="an-nav-links">
            <Link className="an-nav-link is-on" to="/annonces" aria-current="page">Annonces</Link>
            <Link className="an-nav-link" to="/">Comment ça marche</Link>
            {crossLink && <Link className="an-nav-link" to={crossLink.to} data-testid="link-cross-role">{crossLink.label}</Link>}
          </div>
          <div className="an-nav-right">
            {user
              ? <button type="button" className="an-btn-ghost" onClick={() => navigate('/app/dashboard')} data-testid="btn-go-dashboard">Mon espace</button>
              : <button type="button" className="an-btn-ghost" onClick={() => navigate('/login')} data-testid="btn-go-login">Se connecter</button>}
            <PublishButton user={user} buttonClassName="an-btn-primary" testid="btn-publish" shortOnPhone />
          </div>
        </nav>
      </header>

      <main className="an-main">
        <div className="an-top">
          <div>
            <h1 className="an-h1">Annonces</h1>
            <p className="an-sub">Des transporteurs qui partent, et des colis qui attendent un transporteur.</p>
            <p className="an-live">
              <span data-testid="live-count">{stats.active_total}</span> annonces en ligne
              {stats.members > 0 && <> · {stats.members.toLocaleString('fr-FR')} membres</>}
              {stats.delivered > 0 && <> · {stats.delivered.toLocaleString('fr-FR')} colis livrés</>}
            </p>
          </div>
          <div className="an-tabs" role="tablist" aria-label="Type d'annonce">
            {['trajet', 'colis', 'all'].map(k => (
              <button key={k} type="button" role="tab" aria-selected={filterType === k}
                className={`an-tab${filterType === k ? ' is-on' : ''}`}
                onClick={() => setFilterType(k)} data-testid={`filter-type-${k}`}>{TABS[k].tab}</button>
            ))}
          </div>
        </div>

        <form role="search" className="an-search" onSubmit={e => { e.preventDefault(); scrollToResults(); }}>
          <div className="an-field">
            <label htmlFor="an-from">D'où ?</label>
            <input id="an-from" type="text" placeholder="Paris" value={fFrom} onChange={e => setFFrom(e.target.value)} data-testid="filter-from" />
          </div>
          <div className="an-field">
            <label htmlFor="an-to">Vers où ?</label>
            <input id="an-to" type="text" placeholder="Partout" value={fTo} onChange={e => setFTo(e.target.value)} data-testid="filter-to" />
          </div>
          <div className="an-field">
            <label htmlFor="an-date">Avant le</label>
            <input id="an-date" type="date" value={fDate} onChange={e => setFDate(e.target.value)} data-testid="filter-date" />
          </div>
          <div className="an-field">
            <label htmlFor="an-kg">{tab.weight}</label>
            <input id="an-kg" type="number" min="0" step="0.5" inputMode="decimal" placeholder="kg" value={fWeight}
              onChange={e => setFWeight(e.target.value)} data-testid="filter-weight" />
          </div>
          <button type="submit" className="an-search-btn" aria-label="Rechercher" data-testid="btn-hero-search">
            <Search size={22} aria-hidden="true" />
          </button>
        </form>

        <div className="an-cities">
          <span className="an-cities-lbl">Destinations fréquentes :</span>
          {CITIES.map(c => (
            <button key={c} type="button" className="an-city" onClick={() => quickSearch(c)} data-testid={`tag-${c.toLowerCase()}`}>{flag(c)} {c}</button>
          ))}
        </div>

        {filterOpen && <div className="an-backdrop" onClick={() => setFilterOpen(false)} data-testid="sidebar-backdrop" />}
        <aside className={`an-filters${filterOpen ? ' open' : ''}`} data-testid="filters-sidebar" aria-label="Filtres">
          <div className="an-filters-hd">
            <span className="an-filters-title">Filtres</span>
            <button type="button" className="an-reset" onClick={resetFilters} data-testid="btn-reset-filters">Effacer tout</button>
            <button type="button" className="an-close" onClick={() => setFilterOpen(false)} aria-label="Fermer les filtres" data-testid="btn-close-filters"><X size={18} aria-hidden="true" /></button>
          </div>
          <div className="an-chips">
            <label className={`an-chip${fVerif ? ' is-on' : ''}`}><input type="checkbox" checked={fVerif} onChange={e => setFVerif(e.target.checked)} data-testid="filter-verified" />Identité vérifiée</label>
            <label className={`an-chip${fPro ? ' is-on' : ''}`}><input type="checkbox" checked={fPro} onChange={e => setFPro(e.target.checked)} data-testid="filter-pro" />Entreprises</label>
            <label className={`an-chip${fSub ? ' is-on' : ''}`}><input type="checkbox" checked={fSub} onChange={e => setFSub(e.target.checked)} data-testid="filter-subscribed" />Abonnés PRO</label>
            {filterType !== 'colis' && MODES.map(m => (
              <label key={m} className={`an-chip${fModes.includes(m) ? ' is-on' : ''}`}>
                <input type="checkbox" checked={fModes.includes(m)} onChange={() => toggleMode(m)} data-testid={`filter-mode-${m}`} />
                {sansEmoji(TRANSPORT_LABELS[m])}
              </label>
            ))}
            <button type="button" className="an-more-btn" aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)} data-testid="filter-more">
              <SlidersHorizontal size={15} aria-hidden="true" /> {moreOpen ? 'Moins de filtres' : 'Plus de filtres'}
            </button>
          </div>
          <div className={`an-more${moreOpen ? ' open' : ''}`}>
            <div className="an-more-sec">
              <span className="an-more-lbl" id="an-ccy-lbl">
                Devises acceptées{filterCurrencies.length > 0 && <span className="an-more-hint"> ({filterCurrencies.length}/3, la ★ règle le prix)</span>}
              </span>
              {filterCurrencies.length > 0 && (
                <div className="an-ccys">
                  {filterCurrencies.map((ccy, idx) => (
                    <span key={ccy} className={`an-ccy${idx === 0 ? ' is-main' : ''}`}>
                      {idx === 0 && <span aria-hidden="true">★</span>}{ccy}
                      <button type="button" onClick={() => setFilterCurrencies(prev => prev.filter(c => c !== ccy))} aria-label={`Retirer ${ccy}`}><X size={13} aria-hidden="true" /></button>
                    </span>
                  ))}
                </div>
              )}
              {filterCurrencies.length < 3 && (
                <select className="an-select" value="" aria-labelledby="an-ccy-lbl"
                  onChange={e => { const v = e.target.value; if (v) setFilterCurrencies(prev => (prev.includes(v) ? prev : [...prev, v])); }}
                  data-testid="filter-currency">
                  <option value="" disabled>{filterCurrencies.length === 0 ? 'Filtrer par devise…' : 'Ajouter une devise…'}</option>
                  {SUPPORTED_CURRENCIES.filter(c => !filterCurrencies.includes(c.code)).map(c => (
                    <option key={c.code} value={c.code}>{c.code} : {c.name} ({c.symbol})</option>
                  ))}
                </select>
              )}
            </div>
            <div className="an-more-sec">
              <label className="an-more-lbl" htmlFor="an-price">
                Prix max : {maxPrice.toLocaleString('fr-FR')} {ccySym(filterCurrency)}
                {filterCurrency !== 'EUR' && <span className="an-more-hint"> (≈ {Math.round(maxPrice / filterRate).toLocaleString('fr-FR')} €)</span>}
              </label>
              <input id="an-price" type="range" className="an-range" min={sliderMin} max={sliderMax}
                step={ZERO_DEC_CCY.has(filterCurrency) ? 1 : 0.01} value={maxPrice}
                onChange={e => setMaxPrice(+e.target.value)} data-testid="filter-price" />
              <div className="an-range-vals">
                <span>{sliderMin.toLocaleString('fr-FR')} {ccySym(filterCurrency)}</span>
                <span>{sliderMax.toLocaleString('fr-FR')} {ccySym(filterCurrency)}</span>
              </div>
            </div>
          </div>
        </aside>

        <div className="an-results-hd" id="cards-section">
          <p className="an-count">
            <strong data-testid="result-count">{combined.length}</strong> {tab.noun} · {SORT_LABELS[sortBy]}
          </p>
          <label className="an-sort">
            <span>Trier</span>
            <select className="an-select" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="sort-select">
              <option value="recommended">Recommandés</option>
              <option value="recent">Plus récent</option><option value="date-asc">Départ proche</option>
              <option value="price-asc">Prix croissant</option><option value="price-desc">Prix décroissant</option>
              <option value="score-desc">Meilleur score</option>
            </select>
          </label>
        </div>

        {activeChips.length > 0 && (
          <div className="an-active" data-testid="filter-chips">
            {activeChips.map(c => (
              <span key={c.key} className="an-active-chip">
                {c.label}
                <button type="button" onClick={c.clear} aria-label={`Retirer le filtre ${c.label}`} data-testid={`chip-clear-${c.key}`}><X size={13} aria-hidden="true" /></button>
              </span>
            ))}
            <button type="button" className="an-active-reset" onClick={resetFilters}>Tout effacer</button>
          </div>
        )}

        {loading ? (
          <div className="an-grid" data-testid="annonces-skeleton">
            {Array.from({ length: 6 }).map((_, i) => <ModernCardSkeleton key={i} index={i} />)}
          </div>
        ) : loadError ? (
          <div className="an-empty" data-testid="annonces-load-error">
            <CloudOff size={44} strokeWidth={1.5} aria-hidden="true" />
            <h2>Impossible de charger les annonces</h2>
            <p>Vérifiez votre connexion, puis réessayez.</p>
            <button type="button" className="an-btn-primary" onClick={() => setReloadKey(k => k + 1)} data-testid="btn-retry-annonces">Réessayer</button>
          </div>
        ) : combined.length === 0 ? (
          <div className="an-empty" data-testid="empty-state">
            <SearchX size={44} strokeWidth={1.5} aria-hidden="true" />
            <h2>Aucune annonce trouvée</h2>
            <p>Essayez d'élargir vos critères.</p>
            <button type="button" className="an-btn-ghost" onClick={resetFilters}>Effacer les filtres</button>
          </div>
        ) : (
          <>
            <div className="an-grid">
              {combined.map((a, i) => (
                <ModernAnnonceCard key={`${a.type}-${a.id}`} a={a} index={i}
                  isFav={favs.includes(a.id)} onFav={() => toggleFav(a.id, a)}
                  proposalsCount={a.type === 'colis' ? (propCounts.shipments?.[a.id] || 0) : (propCounts.trips?.[a.id] || 0)}
                  onClick={() => navigate('/annonces/' + a.type + '/' + a.id, { state: { annonce: a, fromFilters } })} />
              ))}
            </div>
            {((filterType !== 'trajet' && sHasMore) || (filterType !== 'colis' && tHasMore)) && (
              <div className="an-more-wrap">
                <button type="button" className="an-btn-ghost" onClick={loadMore} disabled={loadingMore} data-testid="btn-load-more">
                  {loadingMore ? <><LoaderCircle size={15} className="an-spin" aria-hidden="true" />Chargement…</> : <><ArrowDownUp size={15} aria-hidden="true" />Charger plus</>}
                </button>
                <span className="an-more-meta">{combined.length} annonces affichées</span>
              </div>
            )}
          </>
        )}

        {/* Encart du bas, dans les deux sens : l'expéditeur publie son colis, le transporteur son trajet */}
        <section className="an-cta" aria-labelledby="an-cta-title">
          <div>
            <h2 id="an-cta-title">{filterType === 'colis' ? 'Pas de colis sur votre trajet ?' : 'Personne ne part à votre date ?'}</h2>
            <p>{filterType === 'colis'
              ? 'Publiez votre trajet : les expéditeurs de ce corridor viendront vous proposer leurs colis.'
              : 'Publiez votre colis : les transporteurs de ce corridor viendront vous proposer un prix.'}</p>
          </div>
          <button type="button" className="an-btn-primary" onClick={() => navigate(publishTarget(user, filterType === 'colis' ? 'trajet' : 'colis'))} data-testid="btn-cta-publish">
            {filterType === 'colis' ? 'Publier mon trajet' : 'Publier mon colis'}
          </button>
        </section>
      </main>

      <button type="button" className={`an-fab${filterOpen ? ' is-hidden' : ''}`} onClick={() => setFilterOpen(true)} aria-label="Ouvrir les filtres" data-testid="filter-fab">
        <SlidersHorizontal size={17} aria-hidden="true" /><span>Filtres</span>
      </button>

      {/* ── Notification favori (ajout ou refus de rôle) ── */}
      {favNotif && (
        <div className={`an-toast${favNotif.kind === 'refused' ? ' is-refused' : ''}`} role="status">
          <div className="an-toast-hd">
            <span className="an-toast-ico" aria-hidden="true">{favNotif.kind === 'refused' ? <X size={14} /> : <Check size={14} />}</span>
            <div className="an-toast-body">
              <div className="an-toast-title">{favNotif.item.from} → {favNotif.item.to}</div>
              <div className="an-toast-text">
                {favNotif.kind === 'refused'
                  ? `Rôle ${favNotif.neededRole === 'ROLE_CARRIER' ? 'Transporteur' : 'Expéditeur'} requis pour ajouter aux favoris`
                  : `${favNotif.item.type === 'colis' ? 'Colis' : 'Trajet'} ajouté à vos favoris`}
              </div>
            </div>
            <button type="button" className="an-toast-close" onClick={() => { if (favNotif.timer) clearTimeout(favNotif.timer); setFavNotif(null); }} aria-label="Fermer"><X size={15} aria-hidden="true" /></button>
          </div>
          {favNotif.kind === 'refused' ? (
            <>
              <p className="an-toast-text">
                Pour mettre cette annonce en favori, il vous faut le rôle{' '}
                <strong>{ROLE_LABELS[favNotif.neededRole]}</strong> : demandez-le, notre équipe valide la demande.
              </p>
              <button type="button" className="an-btn-primary" onClick={() => { setFavNotif(null); navigate(roleRequestPath(favNotif.neededRole)); }}
                data-testid="fav-request-role">
                Demander le rôle {ROLE_LABELS[favNotif.neededRole]}
              </button>
            </>
          ) : (
            <button type="button" className="an-btn-primary" onClick={() => {
              clearTimeout(favNotif.timer);
              setFavNotif(null);
              navigate(favNotif.item.type === 'colis' ? '/app/trips' : '/app/shipments', { state: { openTab: 'favoris' } });
            }}>Voir mes favoris</button>
          )}
        </div>
      )}
    </div>
  );
}
